import React, { useState } from 'react';
import { Row, Col, Form, Button, Badge } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faList, faTrash, faCog } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { useForm } from '../../../hooks/useForm';
import { useFetchSupportForms, useDeleteSupportForm, useFetchBoardConfigs, useDeleteBoardConfig } from '../../../services/supportAdmin';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import DataTable from '../../common/DataTable';
import Pagination from '../../common/Pagination';

import SupportFormModal from './modals/SupportFormModal';
import BoardConfigModal from './modals/BoardConfigModal';

const SupportFormsManager = () => {
  const { form, onChange } = useForm({ search: '' });
  const [filters, setFilters] = useState({
    page: 1,
    search: undefined,
    sortBy: 'formOrder',
    sortDir: 'ASC',
    includeInactive: 'false',
  });
  const [formModal, setFormModal] = useState(false);
  const [boardConfigModal, setBoardConfigModal] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { isLoading, data } = useFetchSupportForms(filters);
  const { isLoading: boardConfigsLoading, data: boardConfigsData } = useFetchBoardConfigs();

  const deleteBoardConfig = useDeleteBoardConfig(
    () => {
      toast.success('Board config has been deleted.');
      queryClient.invalidateQueries('boardConfigs');
    },
    { onError: (err) => toast.error(err.response?.data?.message || err.message) },
  );

  const deleteForm = useDeleteSupportForm(
    () => {
      toast.success('The support form has been deactivated.');
      queryClient.invalidateQueries('supportForms');
    },
    { onError: (err) => toast.error(err.message) },
  );

  const onSubmit = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: form.search, page: 1 });
  };

  const boardConfigs = boardConfigsData?.configs || [];

  return (
    <PrivateLayout>
      <Row className="mb-3">
        <Col>
          <h2>Support Configs</h2>
          <p className="text-muted mb-0">
            Manage board configurations and support forms.
          </p>
        </Col>
      </Row>

      {/* Board Configs Section */}
      <Row className="mb-4">
        <Col>
          <Panel>
            <Row className="align-items-center mb-3">
              <Col>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faCog} className="me-2 text-muted" />
                  Board Configs
                </h5>
              </Col>
              <Col xs="auto">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setBoardConfigModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Board
                </Button>
              </Col>
            </Row>

            <DataTable
              columns={[
                {
                  header: 'Board',
                  key: 'boardName',
                  render: (row) => (
                    <div>
                      <strong>{row.boardName || 'Unnamed Board'}</strong>
                      <br />
                      <small className="text-muted">ID: {row.mondayBoardId}</small>
                    </div>
                  ),
                },
                {
                  header: 'Columns Mapped',
                  key: 'columns',
                  render: (row) => {
                    const mapped = [
                      row.submittedByColumnId,
                      row.emailColumnId,
                      row.filesColumnId,
                    ].filter(Boolean).length;
                    return (
                      <Badge bg={mapped === 3 ? 'success' : mapped > 0 ? 'warning' : 'secondary'}>
                        {mapped}/3
                      </Badge>
                    );
                  },
                },
                {
                  header: 'Status Mappings',
                  key: 'statusMappings',
                  render: (row) => {
                    const closedCount = (row.statusMappings || []).filter(
                      (m) => m.category === 'closed',
                    ).length;
                    return closedCount > 0 ? (
                      <Badge bg="info">{closedCount} closed</Badge>
                    ) : (
                      <Badge bg="secondary">Default</Badge>
                    );
                  },
                },
              ]}
              busy={boardConfigsLoading}
              actions={[
                {
                  label: 'Edit Config',
                  icon: faEdit,
                  onClick: (row) => setBoardConfigModal(row),
                },
                {
                  divider: true,
                },
                {
                  label: 'Delete Config',
                  icon: faTrash,
                  onClick: (row) => {
                    if (
                      window.confirm(
                        `Are you sure you want to delete the config for "${row.boardName || row.mondayBoardId}"? This will not affect existing submissions.`,
                      )
                    ) {
                      deleteBoardConfig.mutate(row.configId);
                    }
                  },
                },
              ]}
              data={boardConfigs}
            />
          </Panel>
        </Col>
      </Row>

      {/* Support Forms Section */}
      <Row>
        <Col>
          <Panel>
            <Row className="align-items-center mb-3">
              <Col>
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faList} className="me-2 text-muted" />
                  Support Forms
                </h5>
              </Col>
              <Col xs="auto">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setFormModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create Form
                </Button>
              </Col>
              <Col xs={12} sm={4}>
                <form onSubmit={onSubmit}>
                  <Form.Control
                    name="search"
                    value={form.search}
                    type="search"
                    placeholder="Search forms..."
                    onChange={onChange}
                    size="sm"
                  />
                </form>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs="auto">
                <Form.Check
                  type="switch"
                  id="include-inactive"
                  label="Show inactive forms"
                  checked={filters.includeInactive === 'true'}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      includeInactive: e.target.checked ? 'true' : 'false',
                      page: 1,
                    })
                  }
                />
              </Col>
            </Row>

            <DataTable
              columns={[
                {
                  header: 'Order',
                  key: 'formOrder',
                  sortable: true,
                  render: (row) => (
                    <span className="text-muted">{row.formOrder}</span>
                  ),
                },
                {
                  header: 'Title',
                  key: 'formTitle',
                  sortable: true,
                  render: (row) => (
                    <div>
                      <strong>{row.formTitle}</strong>
                      <br />
                      <small className="text-muted">/{row.formSlug}</small>
                    </div>
                  ),
                },
                {
                  header: 'Fields',
                  key: 'fieldCount',
                  sortable: false,
                  render: (row) => (
                    <Badge bg="secondary">{row.fieldCount || 0} fields</Badge>
                  ),
                },
                {
                  header: 'Monday Board',
                  key: 'mondayBoardId',
                  sortable: false,
                  render: (row) =>
                    row.mondayBoardId ? (
                      <Badge bg="info">Connected</Badge>
                    ) : (
                      <Badge bg="warning">Not configured</Badge>
                    ),
                },
                {
                  header: 'Status',
                  key: 'formActive',
                  sortable: true,
                  render: (row) =>
                    row.formActive ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    ),
                },
              ]}
              busy={isLoading}
              actions={[
                {
                  label: 'Edit Form',
                  icon: faEdit,
                  onClick: (row) => setFormModal(row),
                },
                {
                  label: 'Manage Fields',
                  icon: faList,
                  onClick: (row) => navigate(`/admin/support-forms/${row.formId}/fields`),
                },
                {
                  divider: true,
                },
                {
                  label: 'Deactivate Form',
                  onClick: (row) => {
                    if (
                      window.confirm(
                        `Are you sure you want to deactivate "${row.formTitle}"? It will no longer appear on the support page.`,
                      )
                    ) {
                      deleteForm.mutate(row.formId);
                    }
                  },
                },
              ]}
              data={data ? data.rows : []}
              defaultSort={`${filters.sortBy}:${filters.sortDir}`}
              onSortChange={(newSortBy, newSortDir) => {
                setFilters({
                  ...filters,
                  page: 1,
                  sortBy: newSortBy,
                  sortDir: newSortDir,
                });
              }}
            />

            {data && data.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={data.total}
                rowsPerPage={50}
                onBack={() =>
                  setFilters({ ...filters, page: filters.page - 1 })
                }
                onForward={() =>
                  setFilters({ ...filters, page: filters.page + 1 })
                }
              />
            ) : null}
          </Panel>
        </Col>
      </Row>

      <SupportFormModal
        show={formModal !== false}
        toggle={() => setFormModal(false)}
        onSuccess={() => queryClient.invalidateQueries('supportForms')}
        supportForm={formModal}
      />

      <BoardConfigModal
        show={boardConfigModal !== false}
        toggle={() => setBoardConfigModal(false)}
        onSuccess={() => queryClient.invalidateQueries('boardConfigs')}
        boardConfig={boardConfigModal}
      />
    </PrivateLayout>
  );
};

export default SupportFormsManager;
